import { useState, type ReactNode } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Star, Share2, Tag, Building2, Lightbulb, ListChecks, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import type { Problem } from '@/lib/api/problems';

const difficultyPill: Record<string, string> = {
  easy: 'bg-green-500/10 text-green-600 dark:text-green-400',
  medium: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
  hard: 'bg-red-500/10 text-red-600 dark:text-red-400',
};

// Difficulty text colours (LeetCode-style) for the Similar Questions list.
const difficultyText: Record<string, string> = {
  easy: 'text-green-600 dark:text-green-400',
  medium: 'text-yellow-600 dark:text-yellow-500',
  hard: 'text-red-500',
};

// Shared prose styling for HTML fragments (statement, constraint items, follow-up).
const proseCls =
  'prose prose-sm max-w-none leading-relaxed dark:prose-invert ' +
  'prose-p:my-3 prose-p:leading-7 prose-strong:font-semibold prose-strong:text-foreground ' +
  'prose-a:text-primary prose-a:no-underline hover:prose-a:underline ' +
  'prose-code:rounded prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 ' +
  'prose-code:font-mono prose-code:text-[0.85em] prose-code:font-normal ' +
  'prose-code:before:content-none prose-code:after:content-none ' +
  // prose-code:* targets every <code>, including ones nested in <pre> code
  // blocks -- without this it overrides Typography's own transparent-bg
  // reset for block code, leaving light-on-light unreadable text.
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-inherit';

// Exported so the editorial renders through exactly the same pipeline as the
// statement — same markdown parser, same GFM/raw-HTML plugins, same prose
// classes. The editorial is prose with inline code and fenced blocks, which is
// what this already handles; a second renderer would only drift from it.
export function Html({ html, className }: { html: string; className?: string }) {
  return (
    <div className={cn(proseCls, className)}>
      <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
        {html}
      </Markdown>
    </div>
  );
}

// AccordionRow is a LeetCode-style full-width collapsible section: an icon + title
// on the left, a chevron on the right, a top divider, and expandable content.
function AccordionRow({
  icon,
  title,
  titleClassName,
  defaultOpen = false,
  children,
}: {
  icon: ReactNode;
  title: ReactNode;
  titleClassName?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 py-3 text-left"
      >
        <span className={cn('flex items-center gap-2 text-[15px] font-medium', titleClassName)}>
          {icon}
          {title}
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </button>
      {/* Indent body content to align under the title text (past the icon). */}
      {open && <div className="pb-4 pl-6">{children}</div>}
    </div>
  );
}

const fmtNum = (n: number) => n.toLocaleString('en-US');
const fmtShort = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : String(n);

// ExampleValue renders one example Input/Output. Single-line values stay
// inline after the label (LeetCode style); multi-line values — stdio pattern
// problems — go in a block below it so newlines and leading spaces survive.
export function ExampleValue({ label, value }: { label: string; value: string }) {
  if (!value.includes('\n')) {
    return (
      <p className="font-mono whitespace-pre-wrap">
        <span className="font-sans font-semibold">{label}: </span>
        {value}
      </p>
    );
  }
  return (
    <div>
      <p className="font-sans font-semibold">{label}:</p>
      <pre className="font-mono whitespace-pre overflow-x-auto">{value}</pre>
    </div>
  );
}

// MetaPill is a LeetCode-style pill (Topics / Companies / Hint) that sits
// right under the title and toggles an inline panel below the pill row.
function MetaPill({
  active,
  onClick,
  icon,
  label,
  className,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors',
        active ? 'border-foreground/15 bg-muted' : 'border-border hover:bg-muted/50',
        className,
      )}
    >
      {icon}
      {label}
    </button>
  );
}

type PanelKey = 'topics' | 'companies' | 'hints';

interface Props {
  problem: Problem;
  slug: string;
}

export function ProblemDescription({ problem, slug }: Props) {
  const [favorited, setFavorited] = useState(false);
  const [activePanel, setActivePanel] = useState<PanelKey | null>(null);
  const togglePanel = (key: PanelKey) => setActivePanel((cur) => (cur === key ? null : key));

  const { title, difficulty, tags = [], content, hints = [], similar_questions = [] } = problem;
  const number = problem.problem_number;
  const companyTags = problem.company_tags ?? [];
  const companyFrequency = problem.company_frequency ?? {};
  const companies = [...companyTags].sort(
    (a, b) => (companyFrequency[b] ?? 0) - (companyFrequency[a] ?? 0),
  );

  const acceptance = problem.acceptance_rate;
  const totalSubs = problem.total_submissions ?? 0;
  const accepted = acceptance != null && totalSubs > 0 ? Math.round((acceptance / 100) * totalSubs) : null;

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/problems/${slug}`);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="problem-workspace p-6">
          {/* Title */}
          <h1 className="mb-3 text-[22px] font-semibold tracking-tight">
            {number != null ? `${number}. ` : ''}
            {title}
          </h1>

          {/* Difficulty + meta pills (Topics / Companies / Hint) */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-medium capitalize',
                difficultyPill[difficulty] || 'bg-muted text-muted-foreground',
              )}
            >
              {difficulty}
            </span>
            {tags.length > 0 && (
              <MetaPill
                active={activePanel === 'topics'}
                onClick={() => togglePanel('topics')}
                icon={<Tag className="h-3.5 w-3.5" />}
                label="Topics"
              />
            )}
            {companies.length > 0 && (
              <MetaPill
                active={activePanel === 'companies'}
                onClick={() => togglePanel('companies')}
                icon={<Building2 className="h-3.5 w-3.5" />}
                label="Companies"
                className={activePanel === 'companies' ? 'text-purple-700 dark:text-purple-400' : undefined}
              />
            )}
            {hints.length > 0 && (
              <MetaPill
                active={activePanel === 'hints'}
                onClick={() => togglePanel('hints')}
                icon={<Lightbulb className="h-3.5 w-3.5" />}
                label="Hint"
              />
            )}
          </div>

          {/* Inline panel for whichever pill is active */}
          {activePanel && (
            <div className="mb-5 rounded-xl border border-border bg-muted/20 p-3">
              {activePanel === 'topics' && (
                <div className="flex flex-wrap gap-1.5" role="list" aria-label="Problem topics">
                  {tags.map((t) => (
                    <span
                      key={t}
                      role="listitem"
                      className="rounded-full bg-muted px-2.5 py-1 text-xs capitalize text-muted-foreground"
                    >
                      {t.replace(/-/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
              {activePanel === 'companies' && (
                <div className="flex flex-wrap gap-1.5" role="list" aria-label="Companies that asked this problem">
                  {companies.map((c) => (
                    <span
                      key={c}
                      role="listitem"
                      className="rounded-full bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700 dark:bg-purple-500/10 dark:text-purple-400"
                    >
                      {c}
                      {companyFrequency[c] > 1 && (
                        <span className="ml-1 text-purple-400 dark:text-purple-500">×{companyFrequency[c]}</span>
                      )}
                    </span>
                  ))}
                </div>
              )}
              {activePanel === 'hints' && (
                <div className="space-y-3">
                  {hints.map((h, i) => (
                    <div key={i}>
                      <p className="mb-1 text-xs font-semibold text-muted-foreground">Hint {i + 1}</p>
                      <Html html={h} className="prose-p:my-0" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Body: structured content when present, else legacy markdown */}
          {content ? (
            <div className="space-y-5">
              {content.statement && <Html html={content.statement} />}

              {content.examples?.map((ex, i) => (
                <div key={i}>
                  <p className="mb-2 text-sm font-semibold">Example {i + 1}:</p>
                  {ex.image && (
                    <img
                      src={ex.image}
                      alt={`Example ${i + 1}`}
                      className="mb-2 max-w-full rounded-md border border-border"
                    />
                  )}
                  <div className="rounded-md border border-border bg-muted/40 px-4 py-3 text-[13px] leading-6">
                    <ExampleValue label="Input" value={ex.input} />
                    <ExampleValue label="Output" value={ex.output} />
                    {ex.explanation && (
                      <p className="font-mono">
                        <span className="font-sans font-semibold">Explanation: </span>
                        {ex.explanation}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {content.constraints?.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-semibold">Constraints:</p>
                  <ul className="list-disc space-y-1.5 pl-6 text-sm text-muted-foreground marker:text-muted-foreground">
                    {content.constraints.map((c, i) => (
                      <li key={i}>
                        <Html html={c} className="prose-p:my-0 [&_p]:inline" />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {content.followUp && (
                <p className="text-sm">
                  <span className="font-semibold">Follow-up: </span>
                  <span dangerouslySetInnerHTML={{ __html: content.followUp }} />
                </p>
              )}
            </div>
          ) : (
            <Html html={problem.description ?? ''} />
          )}

          {/* Stats + collapsible meta sections (LeetCode-style accordion) */}
          <div className="mt-8">
            {/* Accepted / Acceptance rate */}
            {acceptance != null && acceptance > 0 && (
              <div className="flex flex-wrap items-center gap-x-8 gap-y-1 border-t border-border py-3 text-sm">
                {accepted != null && (
                  <span>
                    <span className="text-muted-foreground">Accepted </span>
                    <span className="font-semibold">{fmtNum(accepted)}</span>
                    <span className="text-muted-foreground">/{fmtShort(totalSubs)}</span>
                  </span>
                )}
                <span>
                  <span className="text-muted-foreground">Acceptance Rate </span>
                  <span className="font-semibold">{acceptance.toFixed(1)}%</span>
                </span>
              </div>
            )}

            {/* Similar Questions */}
            {similar_questions.length > 0 && (
              <AccordionRow
                icon={<ListChecks className="h-4 w-4 text-muted-foreground" />}
                title="Similar Questions"
              >
                <ul className="space-y-2.5">
                  {similar_questions.map((q) => (
                    <li key={q.titleSlug} className="flex items-start justify-between gap-4 text-sm">
                      <a
                        href={`/problems/${q.titleSlug}`}
                        className="min-w-0 hover:text-primary hover:underline"
                      >
                        {q.title}
                      </a>
                      <span
                        className={cn(
                          'shrink-0 whitespace-nowrap text-xs capitalize',
                          difficultyText[q.difficulty.toLowerCase()] || 'text-muted-foreground',
                        )}
                      >
                        {q.difficulty}
                      </span>
                    </li>
                  ))}
                </ul>
              </AccordionRow>
            )}
          </div>
        </div>
      </div>

      {/* Action row */}
      <div className="flex shrink-0 items-center gap-1 border-t border-border bg-muted/30 px-4 py-2">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5"
          onClick={() => setFavorited((f) => !f)}
          aria-pressed={favorited}
          aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Star className={cn('h-4 w-4', favorited && 'fill-primary text-primary')} />
          Favorite
        </Button>
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={handleShare} aria-label="Share problem">
          <Share2 className="h-4 w-4" />
          Share
        </Button>
      </div>
    </div>
  );
}
